import parse from 'html-react-parser';
import React from 'react';
import { useParams } from 'react-router-dom';
import TextWithCut from './TextWithCut';
import CatalogItemsGrid from './CatalogItemsGrid';
import Breadcrumbs from './Breadcrumbs';
import GalleryRenderer from '../../helpers/renderer/galleryRenderer';
import InquiryFormRenderer from '../../helpers/renderer/inquiryFormRenderer';
import PosterRenderer from '../../helpers/renderer/posterRenderer';
import BackButtonRenderer from '../../helpers/renderer/backButtonRenderer';
import { useBreadcrumbs } from '../../BreadcrumbsContext';

const emptyPoster = (value) => !value || (typeof value === 'object' && !value.background && !value.backgroundVertical && !value.heading && !value.subheading);

const COLUMN_JUSTIFY = { top: 'flex-start', center: 'center', bottom: 'flex-end' };

// Picks which single 'line' component (if any) should render as this page's <h1> — the
// first heading-or-sub-heading-styled line in document order, unless something earlier in
// that order already renders its own <h1> — a poster (see PosterRenderer) or a rich-text
// block whose editor-authored HTML already contains one — in which case no line should
// compete for it. Returns undefined when nothing claims the page's <h1> at all.
function findH1LineName(components, props) {
    for (const prop of components) {
        if (prop.type === 'poster' && props[prop.name]?.heading) {
            return null;
        }
        if (prop.type === 'widget' && props[prop.name]?.type === 'poster' && props[prop.name]?.config?.heading) {
            return null;
        }
        if (prop.type === 'text') {
            const value = props[prop.name];
            const html = (value && typeof value === 'object') ? value.rendered : value;
            if (typeof html === 'string' && /<h1[\s>]/i.test(html)) {
                return null;
            }
        }
        if (prop.type === 'line') {
            const fontSize = prop.meta?.fontSize;
            const value = props[prop.name];
            if ((fontSize === 'heading' || fontSize === 'sub-heading') && value != null && value !== '') {
                return prop.name;
            }
        }
    }
    return undefined;
}

// Visually identical to not having a heading at all — this is purely so every page has one
// real, unique <h1> for crawlers and screen readers, matching what the <noscript> SSR shell
// already guarantees, without changing how any existing template looks.
const srOnlyStyle = {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
};

function TemplateRows({ rows, mobileColumn, props, isMobile, titleFallback, pageTitle, children }) {
    rows = rows || [];
    mobileColumn = mobileColumn || [];
    props = props || {};
    const params = useParams();
    const currentSlug = params['*'];
    const { breadcrumbs } = useBreadcrumbs();

    const activeComponents = isMobile && mobileColumn.length > 0
        ? mobileColumn
        : rows.flatMap(row => (row.columns || []).flatMap(col => col.components || []));
    const h1LineName = findH1LineName(activeComponents, props);

    const renderProperty = (prop) => {
        const name = prop.name;

        const alignment = prop.alignment || 'left';

        if (prop.type === 'breadcrumbs') {
            // No per-page value — the trail itself always comes from this page's own
            // ancestry (fetched alongside the page, see BreadcrumbsContext), regardless of
            // where in its layout the admin chose to place this component.
            return (
                <div key={name} style={{ textAlign: alignment }}>
                    <Breadcrumbs breadcrumbs={breadcrumbs} />
                </div>
            );
        }

        if (prop.type === 'catalog') {
            // Item layout / elements-in-a-row are this page's own value now (not the
            // layout's), so this always lists whatever children this page actually has,
            // using its own item layout (if one was configured) for title/subtitle.
            const catalogValue = (props[name] && typeof props[name] === 'object') ? props[name] : {};
            const label = prop.show_name ? (
                <div style={{ fontSize: '0.85rem', color: '#777', marginBottom: '4px', textAlign: 'center' }}>{name}</div>
            ) : null;
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%', textAlign: alignment }}>
                    {label}
                    <CatalogItemsGrid
                        children={children}
                        rows={catalogValue.itemLayoutRows || []}
                        elementsInARow={catalogValue.elementsInARow}
                        currentSlug={currentSlug}
                    />
                </div>
            );
        }

        const value = props[name];
        if (value == null || value === "") return null;
        if (Array.isArray(value) && value.length === 0) return null;

        const label = prop.show_name ? (
            <div style={{ fontSize: '0.85rem', color: '#777', marginBottom: '4px', textAlign: 'center' }}>{name}</div>
        ) : null;

        if (prop.type === 'gallery') {
            // Pages saved before the 'image'->'gallery' rename may still hold a plain URL
            // string rather than an array of {src,title}; normalize before passing down.
            const images = Array.isArray(value) ? value : (typeof value === 'string' && value ? [{ src: value, title: '' }] : []);
            if (images.length === 0) return null;
            const comp = { type: 'gallery', images, ...(prop.meta || {}), altFallback: titleFallback };
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%', textAlign: alignment }}>
                    {label}
                    <GalleryRenderer comp={comp} />
                </div>
            );
        }

        if (prop.type === 'poster') {
            if (emptyPoster(value)) return null;
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%', textAlign: alignment }}>
                    {label}
                    <PosterRenderer comp={value} />
                </div>
            );
        }

        if (prop.type === 'line') {
            const isHeadingStyle = prop.meta.fontSize === 'heading' || prop.meta.fontSize === 'sub-heading';
            const fontSize = prop.meta.fontSize === 'heading' ? '2rem' : prop.meta.fontSize === 'sub-heading' ? '1.5rem' : '1rem';
            const Tag = !isHeadingStyle ? 'div' : name === h1LineName ? 'h1' : 'h2';
            return (
                <div key={name} style={{ marginBottom: '16px', textAlign: alignment }}>
                    {label}
                    <Tag style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize, margin: 0 }}>{prop.meta.prefix} {value} {prop.meta.suffix}</Tag>
                </div>
            );
        }

        if (prop.type === 'text') {
            const html = (value && typeof value === 'object') ? value.rendered : value;
            const cut = prop.meta?.cut || 'none';
            return (
                <TextWithCut key={name} cut={cut} value={typeof html === 'string' ? parse(html) : html} label={label} align={alignment} />
            );
        }

        if (prop.type === 'widget') {
            if (!value.widgetId || !value.config) return null;
            const comp = { ...value.config, type: value.type, altFallback: titleFallback };
            if (value.type === 'back_button') {
                const parts = (currentSlug || '').split('/').filter(Boolean);
                if (parts.length <= 1) return null;
                comp.parentPath = parts.slice(0, -1).join('/');
                return (
                    <div key={name} style={{ marginBottom: '16px', width: '100%', textAlign: alignment }}>
                        {label}
                        <BackButtonRenderer comp={comp} />
                    </div>
                );
            }
            const renderer = value.type === 'inquiry_form' ? <InquiryFormRenderer comp={comp} />
                : value.type === 'poster' ? <PosterRenderer comp={comp} />
                : <GalleryRenderer comp={comp} />;
            return (
                <div key={name} style={{ marginBottom: '16px', width: '100%', textAlign: alignment }}>
                    {label}
                    {renderer}
                </div>
            );
        }

        if (prop.type === 'date') {
            const formatted = (() => {
                if (!value) return value;
                const [y, m, d] = value.split('-').map(Number);
                const date = new Date(y, m - 1, d);
                const month = date.toLocaleDateString('en-US', { month: 'short' });
                return `${month} ${date.getDate()}, ${date.getFullYear()}`;
            })();
            return (
                <div key={name} style={{ marginBottom: '16px', textAlign: alignment }}>
                    {label}
                    <div style={{ lineHeight: 1.5 }}>{formatted}</div>
                </div>
            );
        }

        // default: other simple types
        return (
            <div key={name} style={{ marginBottom: '12px', textAlign: alignment }}>
                {label}
                <div>{value}</div>
            </div>
        );
    };

    if (rows.length === 0) return null;

    const fallbackH1 = h1LineName === undefined && pageTitle
        ? <h1 style={srOnlyStyle}>{pageTitle}</h1>
        : null;

    return (
        <>
        {fallbackH1}
        {isMobile && mobileColumn.length > 0 ? (
            <div className="template-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', width: '100%', alignItems: 'start' }}>
                {mobileColumn.map(renderProperty)}
            </div>
        ) : (
            <div className="template-rows" style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
                {rows.map((row, rIdx) => (
                    <div
                        key={rIdx}
                        className="template-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: (row.columns || []).map((c) => `${c.width || (100 / (row.columns.length || 1))}fr`).join(' '),
                            gap: '16px',
                            width: '100%',
                            alignItems: 'start',
                        }}
                    >
                        {(row.columns || []).map((col, cIdx) => (
                            <div key={cIdx} className="template-col" style={{ display: 'flex', flexDirection: 'column', justifyContent: COLUMN_JUSTIFY[col.alignment] || 'flex-start' }}>
                                {(col.components || []).map(renderProperty)}
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        )}
        </>
    );
}

export default TemplateRows;
